import React from 'react';
import { ArrowDownLeft, ArrowUpRight, ChevronDown, Pause, Play, RadioTower, Trash2 } from 'lucide-react';
import {
  EVENT_DIRECTION,
  FEED_FILTER,
  FEED_FILTER_LABEL,
  SEND_TRANSPORT_LABEL,
} from '../../lib/rcu/catalog/link';
import type { FeedFilter } from '../../lib/rcu/catalog/link';
import { TONE } from '../../lib/rcu/catalog/feedback';
import type { Tone } from '../../lib/rcu/catalog/feedback';
import { matchesFilter } from '../../lib/rcu/link/events';
import type { RcuEvent } from '../../lib/rcu/link/events';
import { useRcuLinkStore, FEED_FILTERS } from '../../stores/rcuLinkStore';

export interface EventDrawerProps {
  isOpen: boolean;
  onToggle: () => void;
}

const toneOf = (event: RcuEvent): Tone => {
  if (event.direction === EVENT_DIRECTION.OUT) return event.ok ? TONE.SUCCESS : TONE.ERROR;
  if (event.direction === EVENT_DIRECTION.IN) {
    return event.reading.kind === 'unknown' ? TONE.WARNING : TONE.INFO;
  }
  return TONE.NEUTRAL;
};

const timeOf = (at: string): string => new Date(at).toLocaleTimeString();

const DirectionIcon: React.FC<{ event: RcuEvent }> = ({ event }) => {
  switch (event.direction) {
    case EVENT_DIRECTION.OUT:
      return <ArrowUpRight className="h-4 w-4 shrink-0 text-[#D4A054]" />;
    case EVENT_DIRECTION.IN:
      return <ArrowDownLeft className="h-4 w-4 shrink-0 text-[#4CAF50]" />;
    default:
      return <RadioTower className="h-4 w-4 shrink-0 text-white/50" />;
  }
};

/** The extra detail a row shows once it is opened. */
const EventDetail: React.FC<{ event: RcuEvent }> = ({ event }) => (
  <div className="mt-2 space-y-2 pl-7">
    {event.direction === EVENT_DIRECTION.LINK ? (
      <p className="text-xs text-white/70">{event.detail}</p>
    ) : (
      <>
        <p className="font-mono text-[11px] break-all text-white/50">{event.topic}</p>
        {event.direction === EVENT_DIRECTION.OUT && (
          <p className="text-xs text-white/70">
            {SEND_TRANSPORT_LABEL[event.transport]} · {event.detail}
          </p>
        )}
        <pre className="max-h-48 overflow-auto rounded-xl bg-black/40 p-3 font-mono text-[11px] leading-relaxed text-white/80">
          {event.raw}
        </pre>
      </>
    )}
  </div>
);

/**
 * The event drawer: everything that crossed the link, newest first.
 *
 * It is the console's only window onto acknowledgements, so it stays one tap
 * away and never hides a failure: a refused publish, an unreadable reply and a
 * dropped-message count are all visible here.
 */
export const EventDrawer: React.FC<EventDrawerProps> = ({ isOpen, onToggle }) => {
  const events = useRcuLinkStore((state) => state.events);
  const droppedEvents = useRcuLinkStore((state) => state.droppedEvents);
  const paused = useRcuLinkStore((state) => state.paused);
  const setPaused = useRcuLinkStore((state) => state.setPaused);
  const clearFeed = useRcuLinkStore((state) => state.clearFeed);
  const [filter, setFilter] = React.useState<FeedFilter>(FEED_FILTER.ALL);
  const [openId, setOpenId] = React.useState<string | null>(null);

  const shown = React.useMemo(
    () => events.filter((event) => matchesFilter(event, filter)).slice().reverse(),
    [events, filter],
  );
  const latest = events.at(-1);

  return (
    <section className="pointer-events-none fixed inset-x-0 bottom-0 z-40">
      <div className="pointer-events-auto mx-auto max-w-5xl px-6 pb-4">
        <div className="overflow-hidden rounded-2xl bg-[#1A1A1A] text-white shadow-[0_-4px_24px_rgba(0,0,0,0.25)]">
          <div className="flex items-center gap-3 px-4 py-2.5">
            <button
              type="button"
              onClick={onToggle}
              className="flex min-w-0 flex-1 items-center gap-3 text-left"
            >
              <ChevronDown
                className={`h-4 w-4 shrink-0 text-white/60 transition-transform ${isOpen ? '' : 'rotate-180'}`}
              />
              <span className="text-sm font-medium">Event feed</span>
              <span className="min-w-0 flex-1 truncate text-xs text-white/50">
                {latest === undefined ? 'Nothing yet.' : latest.summary}
              </span>
              <span className="shrink-0 rounded-full bg-white/10 px-2 py-0.5 text-xs text-white/60">
                {events.length}
              </span>
              {droppedEvents > 0 && (
                <span className="shrink-0 rounded-full bg-[#FF9800]/20 px-2 py-0.5 text-xs text-[#FFB74D]">
                  {droppedEvents} dropped
                </span>
              )}
            </button>

            {isOpen && (
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPaused(!paused)}
                  title={paused ? 'Resume the feed' : 'Pause the feed'}
                  className="flex h-8 w-8 items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                >
                  {paused ? <Play className="h-4 w-4" /> : <Pause className="h-4 w-4" />}
                </button>
                <button
                  type="button"
                  onClick={clearFeed}
                  title="Clear the feed"
                  className="flex h-8 w-8 items-center justify-center rounded-full text-white/60 transition-colors hover:bg-white/10 hover:text-white"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            )}
          </div>

          {isOpen && (
            <div className="border-t border-white/10">
              <div className="flex flex-wrap items-center gap-1.5 px-4 py-2">
                {FEED_FILTERS.map((entry) => (
                  <button
                    key={entry}
                    type="button"
                    onClick={() => setFilter(entry)}
                    className={`rounded-full px-3 py-1 text-xs font-medium transition-colors ${
                      entry === filter ? 'bg-[#D4A054] text-[#1A1A1A]' : 'bg-white/10 text-white/70 hover:bg-white/20'
                    }`}
                  >
                    {FEED_FILTER_LABEL[entry]}
                  </button>
                ))}
                {paused && <span className="ml-1 text-xs text-[#FFB74D]">Paused — state still updates.</span>}
              </div>

              <div className="max-h-[42vh] overflow-y-auto px-2 pb-3">
                {shown.length === 0 ? (
                  <p className="px-2 py-6 text-center text-sm text-white/50">
                    {filter === FEED_FILTER.ERRORS
                      ? 'Nothing has failed.'
                      : 'Nothing yet. Read the device or send a command.'}
                  </p>
                ) : (
                  <ul className="space-y-0.5">
                    {shown.map((event) => (
                      <li key={event.id}>
                        <button
                          type="button"
                          onClick={() => setOpenId(openId === event.id ? null : event.id)}
                          className="w-full rounded-xl px-2 py-1.5 text-left transition-colors hover:bg-white/5"
                        >
                          <span className="flex items-baseline gap-2">
                            <span className="shrink-0 font-mono text-[11px] text-white/40">
                              {timeOf(event.at)}
                            </span>
                            <DirectionIcon event={event} />
                            <span className="min-w-0 flex-1 truncate text-sm text-white/85">
                              {event.summary}
                            </span>
                            <span
                              className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] uppercase tracking-wide ${
                                toneOf(event) === TONE.ERROR
                                  ? 'bg-[#F44336]/20 text-[#EF9A9A]'
                                  : 'bg-white/10 text-white/50'
                              }`}
                            >
                              {event.direction}
                            </span>
                          </span>
                          {openId === event.id && <EventDetail event={event} />}
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default EventDrawer;
